import { AtsInterviewDefinitionFragment } from 'src/generated/mloop-graphql';

import { ScorecardGroupByStrategy, TScorecardGroupByKey, TScorecardSortKey } from 'src/hooks/atsService/AtsService';
import { getAtsInterviewDefinitionFields } from 'src/hooks/atsService/util';

type TranslateFn = (key: string) => string;

const ASHBY_SHARED_INTERVIEWS_SORT_INDEX = 0;
const ASHBY_JOB_INTERVIEWS_SORT_INDEX = 1;

/**
 * Returns the translated group header for a scorecard dropdown option.
 */
export const getScorecardGroupName = ({
  option,
  scorecardGroupByStrategy,
  scorecardGroupByKey,
  t,
}: {
  option: AtsInterviewDefinitionFragment;
  scorecardGroupByStrategy: ScorecardGroupByStrategy;
  scorecardGroupByKey: TScorecardGroupByKey;
  t: TranslateFn;
}) => {
  if (!option.atsId) {
    return '';
  }

  switch (scorecardGroupByStrategy) {
    case ScorecardGroupByStrategy.AtsField:
      if (!scorecardGroupByKey) {
        return t('hiring-dashboard-nav:nav.scorecards.noGroup');
      }

      return (
        getAtsInterviewDefinitionFields(option.atsFields, scorecardGroupByKey) ||
        t('hiring-dashboard-nav:nav.scorecards.noGroup')
      );
    case ScorecardGroupByStrategy.AshbyInterviewScope:
      return option.atsJobId
        ? t('hiring-dashboard-nav:nav.scorecards.jobInterviews')
        : t('hiring-dashboard-nav:nav.scorecards.sharedInterviews');
    case ScorecardGroupByStrategy.AtsJobStage:
      return option.atsJobStage?.name || '';
    default: {
      const exhaustiveCheck: never = scorecardGroupByStrategy;
      return exhaustiveCheck;
    }
  }
};

/**
 * Returns a stable sort index so Ashby shared interviews and job interviews render as contiguous groups.
 */
export const getAshbyInterviewScopeSortIndex = (option: AtsInterviewDefinitionFragment) => {
  return option.atsJobId ? ASHBY_JOB_INTERVIEWS_SORT_INDEX : ASHBY_SHARED_INTERVIEWS_SORT_INDEX;
};

/**
 * Returns the ATS-specific primary sort value used by scorecard dropdowns.
 */
export const getScorecardPrimarySortValue = (
  option: AtsInterviewDefinitionFragment,
  scorecardSortKey: TScorecardSortKey
) => {
  if (!scorecardSortKey) {
    return null;
  }

  if (scorecardSortKey === 'index') {
    return `${option.atsJobStage?.index || 0}-${
      getAtsInterviewDefinitionFields(option.atsFields, scorecardSortKey) || 0
    }`;
  }

  return getAtsInterviewDefinitionFields(option.atsFields, scorecardSortKey) || null;
};
